/**
 * AtlasBook — Sprint 4B · the atlas as a flip-through booklet (Phase A)
 * Three nested pages: Europe → country → city. Navigation is robust and
 * gesture-free (confirmed): tap a country/city to drill in, plus lateral
 * ‹ › buttons and a 1·2·3 page indicator — NO swipe, so nothing fights the
 * Leaflet drag on the city page. The page structure is built so a swipe layer
 * (Phase B) could wrap it later without a rewrite.
 *
 * Read-only and isolated: renders the atlas aggregate, never writes app state.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AtlasEuropePage from './AtlasEuropePage.jsx';
import AtlasCountryPage from './AtlasCountryPage.jsx';
import AtlasCityPage from './AtlasCityPage.jsx';
import theme from '../../styles/theme';

export default function AtlasBook() {
  const { t } = useTranslation('common');
  const [level, setLevel] = useState(0);   // 0 Europe · 1 Country · 2 City
  const [country, setCountry] = useState(null);
  const [city, setCity] = useState(null);

  const selectCountry = (c) => { setCountry(c); setCity(null); setLevel(1); };
  const selectCity = (c) => { setCity(c); setLevel(2); };

  // A page number is reachable only if its selection exists.
  const reachable = (lvl) => lvl === 0 || (lvl === 1 && country) || (lvl === 2 && city);
  const go = (lvl) => { if (reachable(lvl)) setLevel(lvl); };

  const crumbs = [
    { lvl: 0, label: t('atlasBook.europe') },
    country && { lvl: 1, label: country },
    city && { lvl: 2, label: city },
  ].filter(Boolean);

  return (
    <div style={S.book}>
      {/* Breadcrumb */}
      <div style={S.crumbs}>
        {crumbs.map((c, i) => (
          <span key={c.lvl}>
            {i > 0 && <span style={S.sep}>›</span>}
            <button type="button" onClick={() => go(c.lvl)}
              style={{ ...S.crumb, ...(c.lvl === level ? S.crumbActive : null) }}>
              {c.label}
            </button>
          </span>
        ))}
      </div>

      {/* Active page, flanked by lateral nav */}
      <div style={S.spread}>
        <button type="button" aria-label={t('atlasBook.prev')} onClick={() => go(level - 1)}
          disabled={level === 0}
          style={{ ...S.arrow, ...(level === 0 ? S.arrowOff : null) }}>‹</button>

        <div style={S.page}>
          {level === 0 && <AtlasEuropePage onSelectCountry={selectCountry} />}
          {level === 1 && country && <AtlasCountryPage country={country} onSelectCity={selectCity} />}
          {level === 2 && country && city && <AtlasCityPage country={country} city={city} />}
        </div>

        <button type="button" aria-label={t('atlasBook.next')} onClick={() => go(level + 1)}
          disabled={!reachable(level + 1)}
          style={{ ...S.arrow, ...(!reachable(level + 1) ? S.arrowOff : null) }}>›</button>
      </div>

      {/* Page numbers (1·2·3) — the booklet's pages */}
      <div style={S.pager}>
        {[0, 1, 2].map(lvl => (
          <button key={lvl} type="button" onClick={() => go(lvl)} disabled={!reachable(lvl)}
            style={{
              ...S.num,
              ...(lvl === level ? S.numActive : null),
              ...(!reachable(lvl) ? S.numOff : null),
            }}>
            {lvl + 1}
          </button>
        ))}
      </div>
    </div>
  );
}

const S = {
  book: { background: theme.card, borderRadius: 18, padding: '16px 12px 12px', boxShadow: '0 2px 14px rgba(0,0,0,0.06)' },
  crumbs: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, marginBottom: 10, paddingLeft: 4, minHeight: 22 },
  crumb: { background: 'transparent', border: 'none', padding: '2px 4px', fontSize: 13, color: theme.brown, cursor: 'pointer', fontWeight: 600 },
  crumbActive: { color: theme.terracottaDark },
  sep: { color: theme.sand, fontSize: 13, margin: '0 2px' },
  spread: { display: 'flex', alignItems: 'center', gap: 4 },
  page: { flex: 1, minWidth: 0 },
  arrow: {
    flex: '0 0 auto', width: 30, height: 56, borderRadius: 10,
    border: 'none', background: 'transparent', color: theme.blueDeep,
    fontSize: 28, lineHeight: 1, cursor: 'pointer', fontWeight: 700,
  },
  arrowOff: { color: theme.creamDeep, cursor: 'default' },
  pager: { display: 'flex', justifyContent: 'center', gap: 10, marginTop: 12 },
  num: {
    width: 28, height: 28, borderRadius: '50%',
    border: `1px solid ${theme.creamDeep}`, background: theme.cream,
    color: theme.brown, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  numActive: { background: theme.terracotta, color: '#fff', borderColor: theme.terracotta },
  numOff: { color: theme.sand, opacity: 0.5, cursor: 'default' },
};
